import { Outlet } from 'react-router-dom';
import { Lock, PlugZap, ShieldAlert, type LucideIcon } from 'lucide-react';
import type { ModuleGate } from '../api/errors';
import { EmptyState } from '@/shared/components/empty-state';
import { PageHeader } from '@/shared/components/page-header';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';

function Gate({
  icon,
  title,
  description,
  pageTitle,
  pageDescription,
  onRetry,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  pageTitle: string;
  pageDescription: string;
  onRetry?: () => void;
}) {
  return (
    <div aria-live="polite">
      <PageHeader title={pageTitle} description={pageDescription} />
      <Card>
        <EmptyState
          icon={icon}
          title={title}
          description={description}
          action={
            onRetry ? (
              <Button type="button" variant="secondary" onClick={onRetry}>
                Tentar de novo
              </Button>
            ) : undefined
          }
        />
      </Card>
    </div>
  );
}

/**
 * Casca do módulo de um canal. `gate` fecha o módulo inteiro quando a API diz
 * que a loja não pode usá-lo (sem permissão, não contratado) ou quando a rota
 * nem existe no servidor (módulo ainda não ligado). Sem gate, as páginas
 * cuidam dos próprios estados de carregamento e erro.
 */
export function ChannelModuleLayout({
  title,
  description,
  gate,
  onRetry,
  deniedTitle,
  deniedDescription,
  uncontractedTitle,
  uncontractedDescription,
  unavailableTitle = 'Módulo ainda não ligado no servidor',
  unavailableDescription,
  workspace = false,
}: {
  title: string;
  description: string;
  gate: ModuleGate | null;
  onRetry?: () => void;
  deniedTitle: string;
  deniedDescription: string;
  uncontractedTitle: string;
  uncontractedDescription: string;
  unavailableTitle?: string;
  unavailableDescription?: string;
  workspace?: boolean;
}) {
  if (gate) {
    const content =
      gate.kind === 'forbidden'
        ? { icon: ShieldAlert, title: deniedTitle, description: deniedDescription, retry: false }
        : gate.kind === 'not_contracted'
          ? { icon: Lock, title: uncontractedTitle, description: uncontractedDescription, retry: false }
          : {
              icon: PlugZap,
              title: unavailableTitle,
              description:
                unavailableDescription ??
                `${gate.message} As telas voltam a carregar sozinhas quando o fixe-api subir com o módulo ligado (marketing.enabled) e a loja contratada.`,
              retry: true,
            };
    const node = (
      <Gate
        icon={content.icon}
        title={content.title}
        description={content.description}
        pageTitle={title}
        pageDescription={description}
        onRetry={content.retry ? onRetry : undefined}
      />
    );
    return workspace ? <div className="min-h-0 flex-1 overflow-auto p-4 lg:p-8">{node}</div> : node;
  }

  if (workspace) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>
    );
  }

  return <Outlet />;
}
